import * as React from 'react';
import { Pressable, View } from 'react-native';
import { cn } from '@/lib/utils';
import { Modal } from './modal';
import { Text } from './text';

interface MultiStepModalProps {
  visible: boolean;
  currentStep: number;
  totalSteps: number;
  onBack?: () => void;
  children: React.ReactNode;
  canGoBack?: boolean;
  title?: string;
  testID?: string;
}

function MultiStepModal({
  visible,
  currentStep,
  totalSteps,
  onBack,
  children,
  canGoBack,
  title,
  testID,
}: MultiStepModalProps) {
  const showBackButton =
    canGoBack !== false && currentStep > 1 && onBack !== undefined;

  const handleClose = () => {
    // Blocking -- cannot be dismissed
  };

  return (
    <Modal
      visible={visible}
      onClose={handleClose}
      backdropPress={false}
      animationType="fade"
      testID={testID}
    >
      <View className="bg-card rounded-xl p-6 min-h-[200px] flex-1">
        <View className="flex-row items-center justify-between mb-4">
          {showBackButton ? (
            <Pressable
              onPress={onBack}
              accessibilityLabel="Go back"
              accessibilityRole="button"
              className="p-1"
            >
              <Text className="text-muted-foreground text-lg">←</Text>
            </Pressable>
          ) : (
            <View className="w-8" />
          )}

          {title && (
            <Text className="text-lg font-semibold text-card-foreground">
              {title}
            </Text>
          )}

          <View className="w-8" />
        </View>

        <View className="flex-row items-center justify-center gap-2 mb-6">
          {Array.from({ length: totalSteps }, (_, i) => {
            const stepNumber = i + 1;
            const isActive = stepNumber === currentStep;
            return (
              <View
                key={stepNumber}
                testID={`step-dot-${stepNumber}`}
                accessibilityLabel={
                  isActive
                    ? `Step ${stepNumber} of ${totalSteps} (current)`
                    : `Step ${stepNumber} of ${totalSteps}`
                }
                className={cn(
                  'w-2 h-2 rounded-full',
                  isActive ? 'bg-primary' : 'bg-muted-foreground/30',
                )}
              />
            );
          })}
        </View>

        <View className="flex-1">{children}</View>
      </View>
    </Modal>
  );
}

export { MultiStepModal };
export type { MultiStepModalProps };
